<template>
  <div>
    <!-- Header -->
    <div class="mb-6">
      <h3 class="text-lg font-medium text-gray-900">
        {{ $t('AI_SETTINGS.SUMMARY.TITLE') }}
      </h3>
      <p class="text-sm text-gray-500">
        {{ $t('AI_SETTINGS.SUMMARY.DESCRIPTION') }}
      </p>
    </div>

    <!-- Enable/Disable Toggle -->
    <div class="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="text-base font-medium text-gray-900">
            {{ $t('AI_SETTINGS.SUMMARY.ENABLE_TOGGLE.TITLE') }}
          </h4>
          <p class="text-sm text-gray-500">
            {{ $t('AI_SETTINGS.SUMMARY.ENABLE_TOGGLE.DESCRIPTION') }}
          </p>
        </div>
        <div class="ml-4">
          <woot-switch
            :model-value="isSummaryEnabled"
            @input="toggleSummaryEnabled"
          />
        </div>
      </div>
    </div>

    <!-- Prompt Settings -->
    <div class="bg-white border border-gray-200 rounded-lg p-6">
      <div class="mb-4">
        <h4 class="text-base font-medium text-gray-900">
          {{ $t('AI_SETTINGS.SUMMARY.PROMPT.TITLE') }}
        </h4>
        <p class="text-sm text-gray-500">
          {{ $t('AI_SETTINGS.SUMMARY.PROMPT.DESCRIPTION') }}
        </p>
      </div>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('AI_SETTINGS.SUMMARY.PROMPT.LABEL') }}
          </label>
          <textarea
            v-model="summaryPrompt"
            :disabled="!isSummaryEnabled"
            :placeholder="$t('AI_SETTINGS.SUMMARY.PROMPT.PLACEHOLDER')"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            rows="6"
            @input="onPromptChange"
          />
        </div>

        <div class="flex justify-end space-x-3">
          <woot-button
            color-scheme="secondary"
            :disabled="!isSummaryEnabled || !hasChanges"
            @click="resetToDefault"
          >
            {{ $t('AI_SETTINGS.SUMMARY.RESET_DEFAULT') }}
          </woot-button>
          <woot-button
            color-scheme="primary"
            :disabled="!isSummaryEnabled || !hasChanges || isLoading"
            :loading="isLoading"
            @click="saveSummarySettings"
          >
            {{ $t('AI_SETTINGS.SUMMARY.SAVE') }}
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables';

export default {
  name: 'ConversationSummaryTab',
  
  data() {
    return {
      isSummaryEnabled: false,
      summaryPrompt: '',
      originalPrompt: '',
      hasChanges: false,
      isLoading: false,
      defaultPrompt: 'Tóm tắt cuộc hội thoại này một cách ngắn gọn và rõ ràng. Bao gồm các điểm chính, vấn đề quan trọng và kết quả (nếu có). Viết bằng tiếng Việt với giọng điệu chuyên nghiệp.',
    };
  },
  
  mounted() {
    this.loadSummarySettings();
  },
  
  methods: {
    async loadSummarySettings() {
      try {
        this.isLoading = true;
        const accountId = this.$store.getters.getCurrentAccountId;
        
        // Load summary settings from API
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/ai_summary_settings`);
        
        if (response.data) {
          this.isSummaryEnabled = response.data.enabled || false;
          this.summaryPrompt = response.data.prompt || this.defaultPrompt;
          this.originalPrompt = this.summaryPrompt;
        } else {
          // Set default values if no settings exist
          this.summaryPrompt = this.defaultPrompt;
          this.originalPrompt = this.summaryPrompt;
        }
      } catch (error) {
        console.error('Error loading summary settings:', error);
        // Set default values on error
        this.summaryPrompt = this.defaultPrompt;
        this.originalPrompt = this.summaryPrompt;
      } finally {
        this.isLoading = false;
      }
    },
    
    onPromptChange() {
      this.hasChanges = this.summaryPrompt !== this.originalPrompt;
    },
    
    resetToDefault() {
      this.summaryPrompt = this.defaultPrompt;
      this.hasChanges = this.summaryPrompt !== this.originalPrompt;
    },
    
    async toggleSummaryEnabled(newValue) {
      console.log('toggleSummaryEnabled called', newValue);
      this.isSummaryEnabled = newValue;
      
      try {
        const accountId = this.$store.getters.getCurrentAccountId;
        console.log('Sending API request with accountId:', accountId);
        
        const response = await window.axios.put(`/api/v1/accounts/${accountId}/ai_summary_settings`, {
          enabled: this.isSummaryEnabled,
          prompt: this.summaryPrompt
        });
        
        console.log('API response:', response);
        const message = this.isSummaryEnabled 
          ? this.$t('AI_SETTINGS.SUMMARY.ENABLED_SUCCESS')
          : this.$t('AI_SETTINGS.SUMMARY.DISABLED_SUCCESS');
        useAlert(message);
      } catch (error) {
        console.error('API error:', error);
        // Revert toggle state on error
        this.isSummaryEnabled = !this.isSummaryEnabled;
        useAlert(this.$t('AI_SETTINGS.SUMMARY.UPDATE_ERROR'));
      }
    },
    
    async saveSummarySettings() {
      try {
        this.isLoading = true;
        const accountId = this.$store.getters.getCurrentAccountId;
        
        await window.axios.put(`/api/v1/accounts/${accountId}/ai_summary_settings`, {
          enabled: this.isSummaryEnabled,
          prompt: this.summaryPrompt
        });
        
        this.originalPrompt = this.summaryPrompt;
        this.hasChanges = false;
        useAlert(this.$t('AI_SETTINGS.SUMMARY.SAVE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('AI_SETTINGS.SUMMARY.SAVE_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script> 